import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, fileUrl } from "../api";
import { byProductName, fmtQty } from "../lib/utils";
import { BrutalButton, BrutalCard, BrutalInput, StockBadge } from "../components/Brutal";
import { PriceTags } from "./InventoryPage";
import AssignProductDialog from "../components/AssignProductDialog";
import { ArrowLeft, Warning, Package, ArrowUUpLeft, PaperPlaneTilt, Gift, CurrencyDollar, Plus, Minus, PencilSimple, Crown, ArrowElbowDownRight, QrCode, Flask, Scales } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";
import { useNasalTiers, isNasalMist, tierUnitPrice, TierBreakdown } from "../components/NasalTiers";
import { SaleSummary } from "../components/SaleSummary";
import { ProductTitle } from "../components/ProductTitle";
import { CodeCardDialog } from "../components/CodeCardDialog";
import { AccountChargesCard } from "../components/AccountCharges";
import { PriceChips } from "../components/PriceChips";
import { GodModePreview } from "../components/GodModePreview";
import { ConvertStockDialog } from "../components/ConvertStockDialog";
import { CoLoginsCard } from "../components/CoLoginsCard";
import { OverrideCommissionCard } from "../components/OverrideCommissionCard";
import { SubDistributorCard } from "../components/SubDistributorCard";
import { BulkSaleDialog } from "../components/BulkSaleDialog";
import { DistributorStatementDialog } from "../components/DistributorStatementDialog";
import UnifiedRequestDialog from "../components/UnifiedRequestDialog";
import ReconcileDialog from "../components/ReconcileDialog";

export default function DistributorDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { isOwner, isSuperOwner, user } = useAuth();
  // v400.34 — Super-owner partners (Harvey / Sukhinder) get the same inventory
  // management rights as Moin: Log Sale, inline stock ±, Give more, Sample,
  // Return, and the payment-status toggle. Only distributor create/delete +
  // settlement writes remain admin-only (enforced server-side).
  const canManage = isOwner || isSuperOwner;
  const [distributor, setDistributor] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [returning, setReturning] = useState(null);
  const [sampling, setSampling] = useState(null);
  const [selling, setSelling] = useState(null);
  const [giving, setGiving] = useState(null); // master product to consign more of
  const [passingDown, setPassingDown] = useState(null); // HELD product to move down to a sub-distributor
  const [converting, setConverting] = useState(null); // v414.16 — producer conversion on holder's behalf
  const [quickGiveOpen, setQuickGiveOpen] = useState(false);
  const [bulkSaleOpen, setBulkSaleOpen] = useState(false); // v414.68
  const [statementOpen, setStatementOpen] = useState(false); // v414.69
  const [saleRequestOpen, setSaleRequestOpen] = useState(false); // v414.71
  const [reconcileOpen, setReconcileOpen] = useState(false); // v414.93

  const load = async () => {
    try {
      const [d, p] = await Promise.all([
        api.get(`/distributors/${id}`),
        api.get(`/distributors/${id}/inventory`),
      ]);
      setDistributor(d.data);
      setProducts(p.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const tm = setInterval(load, 8000);
    return () => clearInterval(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleGiveMore = async (childProduct) => {
    // Open the Give dialog pre-filled with the MASTER product (the owner's source)
    // and this distributor pre-selected and locked.
    const srcId = childProduct.source_product_id;
    if (!srcId) {
      toast.error(t("distributors.no_source_link"));
      return;
    }
    try {
      const { data } = await api.get(`/products/${srcId}`);
      setGiving(data);
    } catch (e) {
      // Most common cause: the master product was deleted/rotated out and the
      // distributor's child row still references it. Surface a friendly hint
      // instead of the raw backend "Product not found".
      const status = e?.response?.status;
      if (status === 404) {
        toast.error(t("distributors.no_source_link"));
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  if (loading) return <p className="font-medium">{t("common.loading")}</p>;
  if (!distributor) return <p className="font-medium">{t("distributors.distributor_not_found")}</p>;

  const lowCount = products.filter((p) => p.starting_stock > 0 && (p.current_stock / p.starting_stock) < 0.4).length;

  return (
    <div className="space-y-6" data-testid="distributor-detail-page">
      <Link to="/distributors" className="inline-flex items-center gap-2 font-medium text-sm hover:underline">
        <ArrowLeft size={16} weight="bold" /> {t("distributors.back")}
      </Link>

      <div className="bg-zinc-950 border border-[#c9a24a] rounded-xl p-6 shadow-sm flex flex-wrap items-center gap-4">
        <div className="w-16 h-16 bg-[#c9a24a] text-zinc-950 rounded-lg flex items-center justify-center font-semibold text-2xl">
          {distributor.name?.[0]?.toUpperCase() || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight truncate text-white">{distributor.name}</h1>
            <DistributorCodeChip distributor={distributor} canEdit={canManage} onChanged={load} />
            {distributor.role === "distributor" && (
              <MarginChip distributor={distributor} canEdit={canManage || distributor.owner_id === user?.id} onChanged={load} />
            )}
            <WellnessMarginChip distributor={distributor} canEdit={canManage || distributor.owner_id === user?.id} onChanged={load} />
            <ParentChip distributor={distributor} canEdit={canManage} onChanged={load} />
            {distributor.is_quick && (
              <span
                className="text-[11px] font-semibold px-2 py-0.5 bg-amber-50 text-amber-900 border border-amber-200 rounded-full"
                data-testid="guest-badge-detail"
              >
                {t("distributors.guest_badge")}
              </span>
            )}
            {distributor.role && distributor.role !== "distributor" ? (
              <span
                className="text-[11px] font-semibold px-2 py-0.5 bg-[#fbf6e7] text-[#8a6a1c] border border-[#c9a24a] rounded-full"
                data-testid="partner-badge-detail"
              >
                {t("distributors.partner_badge")}
              </span>
            ) : (
              <PaymentStatusBadge status={distributor.payment_status || "consignment"} />
            )}
          </div>
          {!distributor.is_quick && (
            <p className="font-medium truncate text-white/90">{distributor.email}</p>
          )}
          {distributor.is_quick && distributor.phone && (
            <p className="font-medium truncate text-white/90">{distributor.phone}</p>
          )}
        </div>
        {canManage && (
          <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
            {distributor.role === "distributor" && (
              <HeaderPaymentToggle
                distributor={distributor}
                onChanged={load}
              />
            )}
            <BrutalButton
              variant="dark"
              onClick={() => setQuickGiveOpen(true)}
              data-testid="quick-give-btn"
              className="max-w-full"
            >
              <PaperPlaneTilt size={16} weight="bold" className="shrink-0" />
              <span className="truncate">{t("distributors.quick_give_btn", { name: distributor.name.split(" ")[0] })}</span>
            </BrutalButton>
          </div>
        )}
      </div>

      {lowCount > 0 && (
        <div className="border border-zinc-200 bg-red-600 text-white rounded-xl p-5 shadow-sm flex items-start gap-3">
          <Warning size={28} weight="fill" />
          <div>
            <h3 className="font-semibold">{t("dashboard.reorder_needed")}</h3>
            <p className="text-sm font-medium">{t("distributors.low_count", { count: lowCount })}</p>
          </div>
        </div>
      )}

      {(canManage || distributor.owner_id === user?.id) && !distributor.is_quick && !distributor.is_personal && (
        <GodModePreview distributor={distributor} limited={!canManage} />
      )}

      {canManage && !distributor.is_quick && !distributor.is_personal && (
        <CoLoginsCard distributorId={id} distributorName={distributor.name} />
      )}

      {isOwner && !distributor.is_quick && !distributor.is_personal && (
        <OverrideCommissionCard distributor={distributor} onChanged={load} />
      )}

      {isOwner && !distributor.is_quick && !distributor.is_personal && (
        <SubDistributorCard distributor={distributor} />
      )}

      {canManage && !distributor.is_quick && !distributor.is_personal && (
        <label
          className="flex items-center gap-3 bg-white border border-zinc-200 rounded-xl px-4 py-3 cursor-pointer"
          data-testid="producer-toggle-row"
        >
          <input
            type="checkbox"
            checked={!!distributor.can_convert}
            onChange={async (e) => {
              try {
                await api.patch(`/distributors/${id}/can-convert`, { enabled: e.target.checked });
                toast.success(e.target.checked
                  ? t("convert.producer_on", { name: distributor.name })
                  : t("convert.producer_off", { name: distributor.name }));
                load();
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }}
            className="w-4 h-4 accent-[#c9a24a]"
            data-testid="producer-toggle-checkbox"
          />
          <span className="text-sm">
            <span className="font-semibold">{t("convert.producer_label")}</span>
            <span className="text-zinc-500"> — {t("convert.producer_hint")}</span>
          </span>
        </label>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-semibold">{canManage ? t("distributors.their_inventory_owner") : t("distributors.their_inventory")}</h2>
        <div className="flex flex-wrap gap-2">
          {/* v414.77 — partners get the request box too (restock only) */}
          {canManage && (distributor?.role === "distributor" || distributor?.role === "partner") && (
            <BrutalButton size="sm" variant="outline" onClick={() => setSaleRequestOpen(true)} data-testid="dist-new-request-btn">
              <CurrencyDollar size={15} weight="bold" /> {t("unified_request.open_btn")}
            </BrutalButton>
          )}
          {canManage && distributor?.role === "distributor" && (
            <BrutalButton size="sm" variant="outline" onClick={() => setStatementOpen(true)} data-testid="dist-statement-btn">
              <CurrencyDollar size={15} weight="bold" /> {t("bulk_sale.statement_btn")}
            </BrutalButton>
          )}
          {canManage && !distributor.is_personal && (
            <BrutalButton size="sm" variant="outline" onClick={() => setReconcileOpen(true)} data-testid="dist-reconcile-btn">
              <Scales size={15} weight="bold" /> {t("distributors.reconcile_btn")}
            </BrutalButton>
          )}
          {canManage && products.some((p) => (p.current_stock || 0) > 0) && (
            <BrutalButton size="sm" onClick={() => setBulkSaleOpen(true)} data-testid="bulk-sale-btn">
              <CurrencyDollar size={15} weight="bold" /> {t("bulk_sale.open_btn")}
            </BrutalButton>
          )}
        </div>
      </div>

      <PersonalUseCard distributorId={id} isPersonalEntry={!!distributor.is_personal} reloadKey={products.length} />

      <AccountChargesCard
        distributorId={id}
        distributorName={distributor.name}
        canCharge={["owner", "admin"].includes(user?.role) || isSuperOwner}
      />

      {products.length === 0 ? (
        <BrutalCard>
          <p className="font-medium">{t("distributors.no_products_assigned")}</p>
        </BrutalCard>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {[...products].sort(byProductName).map((p) => (
            <InventoryRow
              key={p.id}
              product={p}
              distributorId={id}
              showOwnerActions={canManage}
              canPassDown={canManage || user?.id === id}
              paymentStatus={distributor?.payment_status || "consignment"}
              onReturnClick={() => setReturning(p)}
              onSampleClick={() => setSampling(p)}
              onSellClick={() => setSelling(p)}
              onGiveClick={() => handleGiveMore(p)}
              onPassDownClick={() => setPassingDown(p)}
              onConvertClick={canManage && distributor.can_convert ? () => setConverting(p) : null}
              onStockChanged={load}
            />
          ))}
        </div>
      )}

      {returning && (
        <ReturnDialog
          product={returning}
          distributorId={id}
          distributorName={distributor?.name || ""}
          onClose={() => setReturning(null)}
          onDone={() => { setReturning(null); load(); }}
        />
      )}

      {reconcileOpen && (
        <ReconcileDialog
          distributorId={id}
          distributorName={distributor?.name || ""}
          products={products}
          onClose={() => setReconcileOpen(false)}
          onDone={() => { setReconcileOpen(false); load(); }}
        />
      )}

      {(canManage || distributor?.owner_id === user?.id) && user?.id !== id && distributor?.role === "distributor" && (
        <PricingSection distributorId={id} distributorName={distributor?.name || ""} reloadKey={products.length} />
      )}

      {sampling && (
        <SampleOnHolderDialog
          product={sampling}
          holderId={id}
          holderName={distributor?.name || ""}
          onClose={() => setSampling(null)}
          onDone={() => { setSampling(null); load(); }}
        />
      )}

      {selling && (
        <SellOnHolderDialog
          product={selling}
          holderId={id}
          holderName={distributor?.name || ""}
          paymentStatus={distributor?.payment_status || "consignment"}
          onClose={() => setSelling(null)}
          onDone={() => { setSelling(null); load(); }}
        />
      )}

      {bulkSaleOpen && (
        <BulkSaleDialog
          products={products}
          holderId={id}
          holderName={distributor?.name || ""}
          onClose={() => setBulkSaleOpen(false)}
          onDone={() => { setBulkSaleOpen(false); load(); }}
        />
      )}

      {statementOpen && (
        <DistributorStatementDialog
          distributorId={id}
          distributorName={distributor?.name || ""}
          onClose={() => setStatementOpen(false)}
        />
      )}

      {saleRequestOpen && (
        <UnifiedRequestDialog
          onBehalfOf={{ id, name: distributor?.name || "", role: distributor?.role }}
          onClose={() => setSaleRequestOpen(false)}
          onDone={() => load()}
        />
      )}

      {giving && (
        <AssignProductDialog
          product={giving}
          defaultDistributorId={id}
          lockDistributor
          onClose={() => setGiving(null)}
          onAssigned={() => { setGiving(null); load(); }}
        />
      )}

      {passingDown && (
        <AssignProductDialog
          product={passingDown}
          holderUserId={id}
          holderName={distributor?.name || ""}
          onClose={() => setPassingDown(null)}
          onAssigned={() => { setPassingDown(null); load(); }}
        />
      )}

      {converting && (
        <ConvertStockDialog
          product={converting}
          holderId={id}
          onClose={() => setConverting(null)}
          onConverted={() => { setConverting(null); load(); }}
        />
      )}

      {quickGiveOpen && (
        <QuickGiveDialog
          distributorId={id}
          distributorName={distributor?.name || ""}
          onClose={() => setQuickGiveOpen(false)}
          onDone={() => { setQuickGiveOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// v400.96 — Personal-use tracker. On a partner's page it summarizes THEIR
// personal entry (link to it); on the personal entry's own page it is the
// balance card itself. Shows every item ever taken (even fully used) at the
// guideline price ($25 / $35, KLOW & GLOW $40 — per-row editable), so the
// owner sees personal usage even if the partner never pays.
function PersonalUseCard({ distributorId, isPersonalEntry, reloadKey }) {
  const { t } = useTranslation();
  const { user, isSuperOwner } = useAuth();
  const [data, setData] = useState(null);
  const [settling, setSettling] = useState(false); // inline confirm open
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  // v414.13 — super-owner partners (Harvey/Sukhinder) manage personal-use
  // settles too, but never confirm their OWN balance.
  const canSettle = ["owner", "admin", "cfo"].includes(user?.role) ||
    (isSuperOwner && distributorId !== user?.id);

  const refresh = () => {
    api
      .get(`/distributors/${distributorId}/personal-use`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributorId, reloadKey]);

  if (!data?.entry) return null;
  if (!isPersonalEntry && data.items.length === 0 && !data.settled_total) return null;

  const settle = async () => {
    const amt = amount === "" ? null : Number(amount);
    if (amt !== null && (!Number.isFinite(amt) || amt < 0)) {
      toast.error(t("personal_use.invalid_amount"));
      return;
    }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/distributors/${distributorId}/personal-use/settle`, {
        amount: amt === null ? undefined : amt,
      });
      toast.success(t("personal_use.settled_toast", { amount: res.settled_amount.toFixed(2) }));
      setSettling(false);
      setAmount("");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="bg-white border-2 border-[#c9a24a] rounded-xl p-5 shadow-sm"
      data-testid="personal-use-card"
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-semibold text-lg">{t("personal_use.title")}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            {data.tier_info ? t("personal_use.tier_guideline") : t("personal_use.guideline")}
          </p>
          {data.tier_info && data.tier_info.monthly_allowance > 0 && (
            <p
              className={`text-xs font-semibold mt-1 ${
                data.tier_info.used_this_month >= data.tier_info.monthly_allowance
                  ? "text-rose-700"
                  : "text-[#8a6a1c]"
              }`}
              data-testid="personal-tier-usage"
            >
              {t("personal_use.month_usage", {
                used: data.tier_info.used_this_month,
                allowance: data.tier_info.monthly_allowance,
              })}
              {data.tier_info.used_this_month >= data.tier_info.monthly_allowance &&
                ` — ${t("personal_use.month_cap_reached")}`}
            </p>
          )}
        </div>
        {!isPersonalEntry && (
          <Link
            to={`/distributors/${data.entry.id}`}
            className="text-sm font-semibold text-[#8a6a1c] hover:underline shrink-0"
            data-testid="personal-use-view-entry"
          >
            {t("personal_use.view_entry")} →
          </Link>
        )}
      </div>
      {data.items.length === 0 ? (
        <p className="text-sm text-zinc-500 mt-3" data-testid="personal-use-empty">
          {t("personal_use.empty")}
        </p>
      ) : (
        <>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                  <th className="py-1.5 pr-3">{t("personal_use.product")}</th>
                  <th className="py-1.5 pr-3 text-right">{t("personal_use.taken")}</th>
                  <th className="py-1.5 pr-3 text-right">{t("personal_use.used")}</th>
                  <th className="py-1.5 pr-3 text-right">{t("personal_use.on_hand")}</th>
                  <th className="py-1.5 pr-3 text-right">{t("personal_use.price")}</th>
                  <th className="py-1.5 text-right">{t("personal_use.total")}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => (
                  <tr key={it.product_id} className="border-b border-zinc-100" data-testid={`personal-use-row-${it.product_id}`}>
                    <td className="py-2 pr-3 font-medium">{it.name}</td>
                    <td className="py-2 pr-3 text-right">{it.taken}</td>
                    <td className="py-2 pr-3 text-right">{it.used > 0 ? it.used : "—"}</td>
                    <td className="py-2 pr-3 text-right">{it.on_hand}</td>
                    <td className="py-2 pr-3 text-right">${it.unit_price.toFixed(2)}</td>
                    <td className="py-2 text-right font-semibold">${it.line_total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center justify-end gap-2 flex-wrap">
            <span className="text-sm font-medium text-zinc-600">{t("personal_use.total_owed")}</span>
            <span className="text-xl font-semibold" data-testid="personal-use-total">
              ${data.total_owed.toFixed(2)}
            </span>
            {canSettle && data.total_owed > 0 && !settling && (
              <BrutalButton
                size="sm"
                variant="eliteFilled"
                onClick={() => { setAmount(String(data.total_owed.toFixed(2))); setSettling(true); }}
                data-testid="personal-use-settle-btn"
              >
                <CurrencyDollar size={14} weight="bold" /> {t("personal_use.mark_paid")}
              </BrutalButton>
            )}
          </div>
          {settling && (
            <div className="mt-3 rounded-lg border border-[#c9a24a] bg-[#fbf6e7]/60 p-3 flex flex-wrap items-end gap-3" data-testid="personal-use-settle-form">
              <div className="grow max-w-[180px]">
                <label className="block text-xs font-semibold mb-1 text-[#8a6a1c]">{t("personal_use.settle_amount")}</label>
                <BrutalInput
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="personal-use-settle-amount"
                />
              </div>
              <BrutalButton size="sm" onClick={settle} disabled={busy} data-testid="personal-use-settle-confirm">
                {busy ? t("assign.sending") : t("personal_use.settle_confirm")}
              </BrutalButton>
              <BrutalButton size="sm" variant="secondary" onClick={() => setSettling(false)} disabled={busy}>
                {t("common.cancel")}
              </BrutalButton>
              <p className="w-full text-[11px] text-[#8a6a1c]">{t("personal_use.settle_hint")}</p>
            </div>
          )}
        </>
      )}
      {Number(data.settled_total) > 0 && (
        <p className="text-xs text-zinc-500 mt-3" data-testid="personal-use-settled-line">
          {t("personal_use.settled_to_date", { amount: Number(data.settled_total).toFixed(2) })}
          {data.last_settled_at ? ` · ${t("personal_use.last_settled")} ${new Date(data.last_settled_at).toLocaleDateString()}` : ""}
        </p>
      )}
    </section>
  );
}

function MarginChip({ distributor, canEdit, onChanged }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(distributor.margin_surcharge ?? 0);
  const [mtype, setMtype] = useState(distributor.margin_type || "flat");
  const [busy, setBusy] = useState(false);
  const current = Number(distributor.margin_surcharge || 0);
  const currentType = distributor.margin_type || "flat";
  if (current <= 0 && !canEdit) return null;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const n = Math.max(0, Number(value) || 0);
      await api.patch(`/distributors/${distributor.id}/margin`, {
        margin_surcharge: n,
        margin_type: mtype,
      });
      toast.success(mtype === "percent"
        ? t("margin.updated_pct", { n: n.toFixed(0) })
        : t("margin.updated", { n: n.toFixed(2) }));
      setEditing(false);
      onChanged();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <span
        className="inline-flex items-center gap-1.5 text-sm font-bold px-2.5 py-1 bg-emerald-50 text-emerald-900 border-2 border-emerald-400 rounded-lg"
        data-testid="margin-chip"
        title={t("margin.chip_tooltip")}
      >
        {currentType === "percent"
          ? t("margin.chip_label_pct", { n: current.toFixed(current % 1 === 0 ? 0 : 2) })
          : t("margin.chip_label", { n: current.toFixed(2) })}
        {canEdit && (
          <button
            type="button"
            onClick={() => { setValue(current); setMtype(currentType); setEditing(true); }}
            className="text-emerald-800 hover:text-black transition-colors"
            data-testid="edit-margin-btn"
            title={t("margin.edit_title")}
          >
            <PencilSimple size={13} weight="bold" />
          </button>
        )}
      </span>
      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditing(false)}>
          <div
            className="bg-white border-2 border-emerald-400 rounded-xl shadow-xl p-6 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
            data-testid="edit-margin-dialog"
          >
            <h2 className="text-xl font-semibold mb-1">{t("margin.edit_title")}</h2>
            <p className="text-sm text-zinc-600 mb-4">{t("margin.edit_subtitle", { name: distributor.name })}</p>
            <form onSubmit={save} className="space-y-3">
              <div className="grid grid-cols-2 gap-1 rounded-lg border border-zinc-300 p-1">
                {["flat", "percent"].map((mt) => (
                  <button
                    key={mt}
                    type="button"
                    onClick={() => setMtype(mt)}
                    data-testid={`margin-type-${mt}`}
                    className={`h-8 rounded-md text-xs font-bold transition-colors ${mtype === mt ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-800"}`}
                  >
                    {mt === "flat" ? t("margin.type_flat") : t("margin.type_percent")}
                  </button>
                ))}
              </div>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">{mtype === "percent" ? "%" : "$"}</span>
                <BrutalInput
                  type="number"
                  step="0.01"
                  min="0"
                  className="pl-6"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  data-testid="margin-input"
                  required
                />
              </div>
              <p className="text-xs text-zinc-500">{t("margin.edit_hint")}</p>
              <div className="flex gap-3">
                <BrutalButton type="button" variant="secondary" className="flex-1" onClick={() => setEditing(false)}>
                  {t("common.cancel")}
                </BrutalButton>
                <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="margin-save">
                  {busy ? t("common.saving") : t("common.save")}
                </BrutalButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function ParentChip({ distributor, canEdit, onChanged }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [options, setOptions] = useState(null);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  if (distributor.role !== "distributor" || distributor.is_quick || distributor.is_personal) return null;
  if (!distributor.parent_name && !canEdit) return null;

  const openEditor = async () => {
    setChoice("");
    setEditing(true);
    try {
      const { data } = await api.get("/distributors");
      setOptions((data || []).filter((d) =>
        ["distributor", "partner"].includes(d.role) && !d.is_quick && !d.is_personal
        && d.id !== distributor.id && d.id !== distributor.owner_id));
    } catch { setOptions([]); }
  };

  const save = async () => {
    if (!choice) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/distributors/${distributor.id}/parent`, { new_parent_id: choice });
      toast.success(t("distributors.parent_changed", { name: data.parent_name }));
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <span className="inline-flex items-center gap-1.5 text-sm font-bold px-2.5 py-1 rounded-lg border-2 bg-sky-50 text-sky-900 border-sky-300" data-testid="parent-chip">
        <ArrowElbowDownRight size={13} weight="bold" />
        {t("distributors.parent_chip", { name: distributor.parent_name || "Master" })}
        {canEdit && (
          <button type="button" onClick={openEditor} className="text-sky-800 hover:text-black transition-colors"
            data-testid="edit-parent-btn" title={t("distributors.parent_edit_title")}>
            <PencilSimple size={13} weight="bold" />
          </button>
        )}
      </span>
      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditing(false)}>
          <div className="bg-white border-2 border-sky-400 rounded-xl shadow-xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="edit-parent-dialog">
            <h2 className="text-xl font-semibold mb-1">{t("distributors.parent_edit_title")}</h2>
            <p className="text-sm text-zinc-600 mb-4">{t("distributors.parent_edit_sub", { name: distributor.name, parent: distributor.parent_name || "Master" })}</p>
            <select value={choice} onChange={(e) => setChoice(e.target.value)}
              className="w-full border-2 border-zinc-900 rounded-lg px-2 py-2.5 text-sm bg-white mb-2"
              data-testid="parent-select">
              <option value="">{t("distributors.parent_pick")}</option>
              {(options || []).map((d) => (
                <option key={d.id} value={d.id}>{d.name}{d.distributor_code ? ` (${d.distributor_code})` : ""}{d.parent_name ? ` — under ${d.parent_name}` : ""}</option>
              ))}
            </select>
            <p className="text-xs text-zinc-500 mb-4">{t("distributors.parent_edit_hint")}</p>
            <div className="flex gap-3">
              <BrutalButton type="button" variant="secondary" className="flex-1" onClick={() => setEditing(false)}>
                {t("common.cancel")}
              </BrutalButton>
              <BrutalButton type="button" className="flex-1" disabled={busy || !choice} onClick={save} data-testid="parent-save">
                {busy ? t("common.saving") : t("common.save")}
              </BrutalButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function WellnessMarginChip({ distributor, canEdit, onChanged }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(distributor.wellness_margin_surcharge ?? "");
  const [mtype, setMtype] = useState(distributor.wellness_margin_type || "flat");
  const [busy, setBusy] = useState(false);
  const isSet = distributor.wellness_margin_surcharge != null;
  const current = Number(distributor.wellness_margin_surcharge || 0);
  const currentType = distributor.wellness_margin_type || "flat";
  if (!isSet && !canEdit) return null;
  if (!["distributor", "partner"].includes(distributor.role)) return null;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const n = Math.max(0, Number(value) || 0);
      await api.patch(`/distributors/${distributor.id}/wellness-margin`, {
        margin_surcharge: n,
        margin_type: mtype,
      });
      toast.success(mtype === "percent"
        ? t("margin.wellness_updated_pct", { n: n.toFixed(0) })
        : t("margin.wellness_updated", { n: n.toFixed(2) }));
      setEditing(false);
      onChanged();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.patch(`/distributors/${distributor.id}/wellness-margin`, { margin_surcharge: null });
      toast.success(t("margin.wellness_cleared"));
      setEditing(false);
      onChanged();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <span
        className={`inline-flex items-center gap-1.5 text-sm font-bold px-2.5 py-1 rounded-lg border-2 ${isSet ? "bg-teal-50 text-teal-900 border-teal-400" : "bg-white text-zinc-400 border-zinc-200 border-dashed"}`}
        data-testid="wellness-margin-chip"
        title={t("margin.wellness_chip_tooltip")}
      >
        {isSet
          ? (currentType === "percent"
            ? t("margin.wellness_chip_label_pct", { n: current.toFixed(current % 1 === 0 ? 0 : 2) })
            : t("margin.wellness_chip_label", { n: current.toFixed(2) }))
          : t("margin.wellness_unset_chip")}
        {canEdit && (
          <button
            type="button"
            onClick={() => { setValue(isSet ? current : ""); setMtype(currentType); setEditing(true); }}
            className={`${isSet ? "text-teal-800" : "text-zinc-400"} hover:text-black transition-colors`}
            data-testid="edit-wellness-margin-btn"
            title={t("margin.wellness_edit_title")}
          >
            <PencilSimple size={13} weight="bold" />
          </button>
        )}
      </span>
      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditing(false)}>
          <div
            className="bg-white border-2 border-teal-400 rounded-xl shadow-xl p-6 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
            data-testid="edit-wellness-margin-dialog"
          >
            <h2 className="text-xl font-semibold mb-1">{t("margin.wellness_edit_title")}</h2>
            <p className="text-sm text-zinc-600 mb-4">{t("margin.wellness_edit_subtitle", { name: distributor.name })}</p>
            <form onSubmit={save} className="space-y-3">
              <div className="grid grid-cols-2 gap-1 rounded-lg border border-zinc-300 p-1">
                {["flat", "percent"].map((mt) => (
                  <button
                    key={mt}
                    type="button"
                    onClick={() => setMtype(mt)}
                    data-testid={`wellness-margin-type-${mt}`}
                    className={`h-8 rounded-md text-xs font-bold transition-colors ${mtype === mt ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-800"}`}
                  >
                    {mt === "flat" ? t("margin.type_flat") : t("margin.type_percent")}
                  </button>
                ))}
              </div>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">{mtype === "percent" ? "%" : "$"}</span>
                <BrutalInput
                  type="number"
                  step="0.01"
                  min="0"
                  className="pl-6"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  data-testid="wellness-margin-input"
                  required
                />
              </div>
              <p className="text-xs text-zinc-500">{t("margin.wellness_edit_hint")}</p>
              <div className="flex gap-3">
                <BrutalButton type="button" variant="secondary" className="flex-1" onClick={() => setEditing(false)}>
                  {t("common.cancel")}
                </BrutalButton>
                <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="wellness-margin-save">
                  {busy ? t("common.saving") : t("common.save")}
                </BrutalButton>
              </div>
              {isSet && (
                <button type="button" onClick={clear} disabled={busy}
                  className="w-full text-xs font-semibold text-zinc-500 hover:text-red-600 transition-colors pt-1"
                  data-testid="wellness-margin-clear">
                  {t("margin.wellness_clear_btn")}
                </button>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function DistributorCodeChip({ distributor, canEdit, onChanged }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [code, setCode] = useState(distributor.distributor_code || "");
  const [busy, setBusy] = useState(false);
  if (!distributor.distributor_code && !canEdit) return null;

  const save = async (e) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setBusy(true);
    try {
      await api.patch(`/distributors/${distributor.id}/code`, { code: trimmed });
      toast.success(t("dist_code.updated", { code: trimmed }));
      setEditing(false);
      onChanged();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <span
        className="inline-flex items-center gap-1.5 text-sm font-bold px-2.5 py-1 bg-[#fbf6e7] text-[#8a6a1c] border-2 border-[#c9a24a] rounded-lg font-mono tracking-widest"
        data-testid="distributor-code-chip"
      >
        {distributor.distributor_code || t("dist_code.none")}
        {distributor.distributor_code && (
          <button
            type="button"
            onClick={() => setShowCard(true)}
            className="text-[#8a6a1c] hover:text-black transition-colors"
            data-testid="open-code-card-btn"
            title={t("code_card.title")}
          >
            <QrCode size={13} weight="bold" />
          </button>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={() => { setCode(distributor.distributor_code || ""); setEditing(true); }}
            className="text-[#8a6a1c] hover:text-black transition-colors"
            data-testid="edit-code-btn"
            title={t("dist_code.edit_title")}
          >
            <PencilSimple size={13} weight="bold" />
          </button>
        )}
      </span>
      {showCard && (
        <CodeCardDialog distributor={distributor} onClose={() => setShowCard(false)} />
      )}
      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditing(false)}>
          <div
            className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
            data-testid="edit-code-dialog"
          >
            <h2 className="text-xl font-semibold mb-1">{t("dist_code.edit_title")}</h2>
            <p className="text-sm text-zinc-600 mb-4">{t("dist_code.edit_subtitle")}</p>
            <form onSubmit={save} className="space-y-3">
              <BrutalInput
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="HP-01"
                maxLength={12}
                className="font-mono tracking-widest uppercase"
                data-testid="code-input"
                required
              />
              <div className="flex gap-3">
                <BrutalButton type="button" variant="secondary" className="flex-1" onClick={() => setEditing(false)}>
                  {t("common.cancel")}
                </BrutalButton>
                <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="code-save">
                  {busy ? t("common.saving") : t("common.save")}
                </BrutalButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function ReturnDialog({ product, distributorId, distributorName, onClose, onDone }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // v400.63: for partner/distributor callers the stock lands in THEIR held
  // stock (one level up), not master — the copy must match.
  const heldReturn = user?.role === "partner" || user?.role === "distributor";
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const n = Math.max(1, Number(qty) || 0);
    setBusy(true);
    try {
      await api.post("/products/return", {
        distributor_id: distributorId,
        product_id: product.id,
        quantity: n,
      });
      toast.success(t("return.taken_back", { n, name: product.name }));
      onDone();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="return-dialog"
      >
        <h2 className="text-xl font-semibold mb-1">{t("return.dialog_title")}</h2>
        <p className="text-sm text-zinc-600 mb-4">
          {t(heldReturn ? "return.dialog_subtitle_held" : "return.dialog_subtitle", { name: product.name, distributor: distributorName, n: product.current_stock })}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("return.quantity")}</label>
            <BrutalInput
              type="number"
              min="1"
              max={product.current_stock}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              data-testid="return-quantity-input"
            />
            {Number(qty) > product.current_stock && (
              <p className="text-xs text-amber-700 mt-1" data-testid="return-unaccounted-hint">
                {t("return.unaccounted_hint", {
                  extra: Number(qty) - product.current_stock,
                  n: product.current_stock,
                })}
              </p>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="return-submit">
              {busy ? t("return.processing") : t(heldReturn ? "return.confirm_held" : "return.confirm")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function SampleOnHolderDialog({ product, holderId, holderName, onClose, onDone }) {
  const { t } = useTranslation();
  const [qty, setQty] = useState(1);
  const [recipient, setRecipient] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const name = recipient.trim();
    if (!name) {
      toast.error(t("assign.recipient_required"));
      return;
    }
    const n = Math.max(1, Number(qty) || 0);
    if (n > product.current_stock) {
      toast.error(t("assign.only_n_on_hand", { n: product.current_stock }));
      return;
    }
    setBusy(true);
    try {
      await api.post("/products/mark-sample", {
        product_id: product.id,
        quantity: n,
        recipient_name: name,
        holder_user_id: holderId,
      });
      toast.success(t("assign.sampled_n", { n, name: product.name }));
      onDone();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="sample-holder-dialog"
      >
        <h2 className="text-xl font-semibold mb-1">{t("assign.mark_sample")}</h2>
        <p className="text-sm text-zinc-600 mb-4">
          {t("distributors.sample_on_holder_subtitle", { holder: holderName, name: product.name, n: product.current_stock })}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("assign.recipient_name")}</label>
            <BrutalInput
              type="text"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder={t("assign.recipient_placeholder")}
              maxLength={200}
              data-testid="sample-holder-recipient-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("assign.quantity")}</label>
            <BrutalInput
              type="number"
              min="1"
              max={product.current_stock}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              data-testid="sample-holder-quantity-input"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="sample-holder-submit">
              {busy ? t("assign.sending") : t("assign.log_sample")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function SellOnHolderDialog({ product, holderId, holderName, paymentStatus = "consignment", onClose, onDone }) {
  const { t } = useTranslation();
  const [qty, setQty] = useState(1);
  const [recipient, setRecipient] = useState("");
  const [salePrice, setSalePrice] = useState(
    product?.suggested_retail_price != null ? String(product.suggested_retail_price) : ""
  );
  // v44: Precision Nasal Mist sales use the tiered SRP table — the unit price
  // auto-follows the quantity until the seller edits it manually.
  const tiers = useNasalTiers();
  const mist = isNasalMist(product);
  const [priceDirty, setPriceDirty] = useState(false);
  const onQtyChange = (v) => {
    setQty(v);
    if (mist && !priceDirty) {
      const p = tierUnitPrice(tiers, v);
      if (p != null) setSalePrice(String(p));
    }
  };
  // v400.58 editable "Wholesale Price (Cost)" — prefilled from the holder's
  // effective wholesale (their price incl. surcharge) or the catalog price.
  const [costBasis, setCostBasis] = useState(
    product?.wholesale_price != null ? String(product.wholesale_price) : ""
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/distributors/${holderId}/pricing`)
      .then(({ data }) => {
        const row = (data || []).find((r) => r.product_id === product.id);
        const v = row?.their_wholesale ?? row?.base_wholesale;
        if (v != null) setCostBasis(String(v));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, holderId]);

  const submit = async (e) => {
    e.preventDefault();
    const n = Math.max(1, Number(qty) || 0);
    if (n > product.current_stock) {
      toast.error(t("assign.only_n_on_hand", { n: product.current_stock }));
      return;
    }
    const sp = salePrice === "" ? null : Number(salePrice);
    if (sp !== null && (!Number.isFinite(sp) || sp < 0)) {
      toast.error(t("assign.invalid_sale_price"));
      return;
    }
    const cb = costBasis === "" ? null : Number(costBasis);
    if (cb !== null && (!Number.isFinite(cb) || cb < 0)) {
      toast.error(t("assign.invalid_sale_price"));
      return;
    }
    setBusy(true);
    try {
      const payload = {
        product_id: product.id,
        quantity: n,
        holder_user_id: holderId,
        // Auto-append the sale into the Settlement Ledger. Backend decides
        // pending_payout (consignment) vs paid_out audit row (paid).
        create_settlement: true,
      };
      if (recipient.trim()) payload.recipient_name = recipient.trim();
      if (sp !== null) payload.sale_price = sp;
      if (cb !== null) payload.wholesale_unit_override = cb;
      await api.post("/products/mark-sold", payload);
      toast.success(
        paymentStatus === "consignment"
          ? t("distributors.sale_logged_pending", { n, name: product.name })
          : t("assign.sold_n", { n, name: product.name })
      );
      onDone();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const unitSale = Number(salePrice) || 0;
  const unitCost = Number(costBasis) || 0;
  const nQty = Number(qty) || 0;
  const total = unitSale * nQty;
  const unitMargin = Math.max(0, unitSale - unitCost);
  const pendingPayout = Math.max(0, (unitSale - unitCost) * nQty);
  const isConsignment = paymentStatus === "consignment";

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="sell-holder-dialog"
      >
        <h2 className="text-xl font-semibold mb-1">{t("assign.mark_sold")}</h2>
        <p className="text-sm text-zinc-600 mb-4">
          {t("distributors.sell_on_holder_subtitle", { holder: holderName, name: product.name, n: product.current_stock })}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("assign.quantity")}</label>
            <BrutalInput
              type="number"
              min="1"
              max={product.current_stock}
              value={qty}
              onChange={(e) => onQtyChange(e.target.value)}
              data-testid="sell-holder-quantity-input"
            />
          </div>
          {mist && <TierBreakdown tiers={tiers} qty={qty} />}
          <div>
            <label className="block text-xs font-semibold mb-1">
              {t("assign.sale_price_label", { unit: product.unit || t("common.units") })}
            </label>
            <BrutalInput
              type="number"
              min="0"
              step="0.01"
              value={salePrice}
              onChange={(e) => {
                setPriceDirty(true);
                setSalePrice(e.target.value);
              }}
              placeholder={product.suggested_retail_price != null ? String(product.suggested_retail_price) : "0.00"}
              data-testid="sell-holder-sale-price-input"
              autoFocus
            />
            <PriceChips
              wholesale={costBasis !== "" ? costBasis : product.wholesale_price}
              retail={product.suggested_retail_price}
              value={salePrice}
              onPick={(v) => { setPriceDirty(true); setSalePrice(v); }}
              testIdPrefix="sell-holder-price-chip"
            />
            <p className="text-[11px] text-zinc-500 mt-1">
              {product.suggested_retail_price != null
                ? t("assign.sale_price_hint_srp", { srp: product.suggested_retail_price })
                : t("assign.sale_price_hint_no_srp")}
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("distributors.cost_basis_label")}</label>
            <BrutalInput
              type="number"
              min="0"
              step="0.01"
              value={costBasis}
              onChange={(e) => setCostBasis(e.target.value)}
              placeholder="0.00"
              data-testid="sell-holder-cost-input"
            />
            <p className="text-[11px] text-zinc-500 mt-1">{t("distributors.cost_basis_hint")}</p>
          </div>
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 flex items-baseline justify-between">
            <span className="text-xs text-zinc-600">{t("receivables.implied_total")}</span>
            <span className="text-base font-semibold tabular-nums" data-testid="sell-holder-implied-total">
              {Number.isFinite(total) ? `$${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
            </span>
          </div>
          {isConsignment && Number.isFinite(pendingPayout) && (
            <div
              className="bg-[#fbf6e7] border-2 border-[#c9a24a] rounded-lg p-3 flex items-baseline justify-between"
              data-testid="sell-holder-pending-payout-preview"
            >
              <div className="text-xs">
                <div className="text-[#8a6a1c] font-semibold">{t("distributors.pending_payout_label")}</div>
                <div className="text-[10px] text-[#8a6a1c]/70">
                  {t("distributors.pending_payout_hint", {
                    sale: `$${unitSale.toFixed(2)}`,
                    cost: `$${unitCost.toFixed(2)}`,
                    margin: `$${unitMargin.toFixed(2)}`,
                    n: nQty,
                    payout: `$${pendingPayout.toFixed(2)}`,
                  })}
                </div>
              </div>
              <span className="text-base font-semibold tabular-nums text-[#8a6a1c]">
                ${pendingPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold mb-1">
              {t("distributors.sell_recipient_label")}
            </label>
            <BrutalInput
              type="text"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder={t("distributors.sell_recipient_placeholder")}
              maxLength={200}
              data-testid="sell-holder-recipient-input"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="sell-holder-submit">
              {busy ? t("assign.sending") : t("assign.mark_sold")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function PricingSection({ distributorId, distributorName, reloadKey }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [markup, setMarkup] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api
      .get(`/distributors/${distributorId}/pricing`)
      .then(({ data }) => setRows(data))
      .catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [distributorId, reloadKey]);

  const save = async (pid) => {
    setBusy(true);
    try {
      await api.post(`/distributors/${distributorId}/pricing/${pid}`, {
        markup_per_unit: Number(markup) > 0 ? Number(markup) : 0,
      });
      toast.success(t("distributors.pricing_updated"));
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!rows.length) return null;
  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm" data-testid="pricing-section">
      <h2 className="text-lg font-semibold tracking-tight">
        {t("distributors.pricing_title", { name: distributorName })}
      </h2>
      <p className="text-xs text-zinc-500 mb-3">{t("distributors.pricing_subtitle")}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-zinc-500">
              <th className="py-1.5 pr-3 font-semibold">{t("distributors.pricing_col_product")}</th>
              <th className="py-1.5 pr-3 font-semibold">{t("distributors.pricing_col_base")}</th>
              <th className="py-1.5 pr-3 font-semibold">{t("distributors.pricing_col_their")}</th>
              <th className="py-1.5 pr-3 font-semibold">{t("distributors.pricing_col_markup")}</th>
              <th className="py-1.5 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {[...rows].sort(byProductName).map((r) => (
              <tr key={r.product_id} className="border-t border-zinc-100 align-middle" data-testid={`pricing-row-${r.product_id}`}>
                <td className="py-2 pr-3 font-medium">
                  {r.name} <span className="text-xs text-zinc-400">×{r.current_stock}</span>
                </td>
                <td className="py-2 pr-3 tabular-nums text-zinc-600">
                  {r.base_wholesale != null ? `$${Number(r.base_wholesale).toFixed(2)}` : "—"}
                </td>
                <td className="py-2 pr-3 tabular-nums">
                  {r.their_wholesale != null ? (
                    <span className="font-semibold">${Number(r.their_wholesale).toFixed(2)}</span>
                  ) : (
                    <span className="text-[11px] font-semibold px-2 py-0.5 bg-zinc-100 text-zinc-500 border border-zinc-200 rounded-full">
                      {t("distributors.pricing_hidden")}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-3 tabular-nums text-emerald-700">
                  {Number(r.markup_per_unit) > 0 ? `+$${Number(r.markup_per_unit).toFixed(2)}` : "—"}
                </td>
                <td className="py-2 text-right">
                  {editing === r.product_id ? (
                    <div className="flex items-center gap-1.5 justify-end">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="input-brutal w-24 text-sm py-1"
                        value={markup}
                        onChange={(e) => setMarkup(e.target.value)}
                        placeholder="0.00"
                        data-testid={`pricing-markup-input-${r.product_id}`}
                        autoFocus
                      />
                      <BrutalButton size="sm" onClick={() => save(r.product_id)} disabled={busy} data-testid={`pricing-save-${r.product_id}`}>
                        {t("distributors.pricing_save")}
                      </BrutalButton>
                    </div>
                  ) : (
                    <BrutalButton
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setEditing(r.product_id);
                        setMarkup(Number(r.markup_per_unit) > 0 ? String(r.markup_per_unit) : "");
                      }}
                      data-testid={`pricing-edit-${r.product_id}`}
                    >
                      <PencilSimple size={13} weight="bold" /> {t("distributors.pricing_edit")}
                    </BrutalButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-zinc-400 mt-2">{t("distributors.pricing_hide_hint")}</p>
    </section>
  );
}

function InventoryRow({ product, distributorId, showOwnerActions, canPassDown, paymentStatus, onReturnClick, onSampleClick, onSellClick, onGiveClick, onPassDownClick, onConvertClick, onStockChanged }) {
  const { t } = useTranslation();
  const percent = product.starting_stock > 0 ? (product.current_stock / product.starting_stock) * 100 : 0;
  const low = percent < 40;
  const isSold = (product.last_transfer_type || "") === "sale";
  const isSample = (product.last_transfer_type || "") === "sample";
  const hasStock = product.current_stock > 0;
  return (
    <div
      data-testid={`dist-product-row-${product.id}`}
      className={`bg-white border-2 ${low ? "border-red-300" : "border-black"} rounded-xl p-3 shadow-sm flex gap-3`}
    >
      {product.photo_path && (
        <img
          src={fileUrl(product.photo_path)}
          alt={product.name}
          className="w-14 h-14 border border-zinc-200 rounded-lg object-cover shrink-0"
          crossOrigin="use-credentials"
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <ProductTitle name={product.name} id={product.id} className="font-semibold text-base truncate" />
          <StockBadge percent={percent} />
        </div>
        <div className="mt-1.5 flex items-baseline gap-1.5 flex-wrap">
          <span className="text-xl font-semibold leading-none">{fmtQty(product.current_stock)}</span>
          <span className="text-xs text-zinc-600">/ {fmtQty(product.starting_stock)} {product.unit || t("common.units")}</span>
          {isSold && (
            <span className="ml-1 text-[10px] font-medium px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
              {t("assign.mark_sold")}
            </span>
          )}
          {isSample && (
            <span className="ml-1 text-[10px] font-medium px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
              {t("assign.mark_sample")}
            </span>
          )}
        </div>
        <div className="h-1.5 mt-1.5 bg-zinc-100 border border-zinc-200 rounded-full overflow-hidden">
          <div className={`h-full ${low ? "bg-red-600" : "bg-[#c9a24a]"}`} style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
        </div>
        <PriceTags product={product} className="!mt-1.5" />
        {showOwnerActions && (
          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
            <BrutalButton
              size="sm"
              variant="eliteFilled"
              onClick={onSellClick}
              disabled={!hasStock}
              data-testid={`log-sale-btn-${product.id}`}
            >
              <CurrencyDollar size={16} weight="bold" /> {t("distributors.log_sale_btn")}
            </BrutalButton>
            <InlineStockAdjuster
              product={product}
              distributorId={distributorId}
              onChanged={onStockChanged}
            />
            <div className="flex-1" />
            <BrutalButton
              size="sm"
              variant="secondary"
              onClick={onGiveClick}
              data-testid={`give-more-btn-${product.id}`}
            >
              <PaperPlaneTilt size={14} weight="bold" /> {t("distributors.give_more")}
            </BrutalButton>
            <BrutalButton
              size="sm"
              variant="secondary"
              onClick={onSampleClick}
              disabled={!hasStock}
              data-testid={`sample-btn-${product.id}`}
            >
              <Gift size={14} weight="bold" /> {t("assign.mark_sample")}
            </BrutalButton>
            <BrutalButton
              size="sm"
              variant="secondary"
              onClick={onReturnClick}
              disabled={!hasStock}
              data-testid={`return-btn-${product.id}`}
            >
              <ArrowUUpLeft size={14} weight="bold" /> {t("return.button")}
            </BrutalButton>
            {onConvertClick && (product.category || "Injectable") === "Injectable" && (
              <BrutalButton
                size="sm"
                variant="secondary"
                onClick={onConvertClick}
                disabled={!hasStock}
                data-testid={`convert-btn-${product.id}`}
              >
                <Flask size={14} weight="bold" /> {t("convert.button")}
              </BrutalButton>
            )}
          </div>
        )}
        {canPassDown && (
          <div className="mt-2 flex flex-wrap justify-end gap-1.5">
            <BrutalButton
              size="sm"
              variant="secondary"
              onClick={onPassDownClick}
              disabled={!hasStock}
              data-testid={`pass-down-btn-${product.id}`}
            >
              <ArrowElbowDownRight size={14} weight="bold" /> {t("assign.pass_down")}
            </BrutalButton>
          </div>
        )}
      </div>
    </div>
  );
}

function InlineStockAdjuster({ product, distributorId, onChanged }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const bump = async (delta) => {
    if (busy) return;
    if (delta < 0 && product.current_stock <= 0) return;
    setBusy(true);
    try {
      await api.post(`/admin/distributor-stock/${distributorId}/${product.id}/adjust`, { delta });
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-1 rounded-lg border-2 border-[#c9a24a] bg-white overflow-hidden">
      <button
        type="button"
        disabled={busy || product.current_stock <= 0}
        onClick={() => bump(-1)}
        data-testid={`stock-minus-${product.id}`}
        className="h-8 w-8 flex items-center justify-center text-[#8a6a1c] hover:bg-[#fbf6e7] disabled:opacity-40 transition-colors"
        aria-label={t("distributors.stock_decrement")}
        title={t("distributors.stock_decrement")}
      >
        <Minus size={14} weight="bold" />
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => setShowEdit(true)}
        data-testid={`stock-edit-${product.id}`}
        className="h-8 px-2 flex items-center gap-1 text-[10px] font-semibold text-[#8a6a1c] hover:bg-[#fbf6e7] transition-colors uppercase tracking-wide"
        title={t("distributors.stock_edit")}
      >
        <PencilSimple size={12} weight="bold" />
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => bump(1)}
        data-testid={`stock-plus-${product.id}`}
        className="h-8 w-8 flex items-center justify-center text-[#8a6a1c] hover:bg-[#fbf6e7] disabled:opacity-40 transition-colors"
        aria-label={t("distributors.stock_increment")}
        title={t("distributors.stock_increment")}
      >
        <Plus size={14} weight="bold" />
      </button>
      {showEdit && (
        <EditStockDialog
          product={product}
          distributorId={distributorId}
          onClose={() => setShowEdit(false)}
          onDone={() => { setShowEdit(false); onChanged(); }}
        />
      )}
    </div>
  );
}

function EditStockDialog({ product, distributorId, onClose, onDone }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(String(product.current_stock));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const target = Math.max(0, Math.floor(Number(value) || 0));
    const delta = target - product.current_stock;
    if (delta === 0) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/distributor-stock/${distributorId}/${product.id}/adjust`, { delta });
      toast.success(t("distributors.stock_updated", { name: product.name, n: target }));
      onDone();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border-2 border-[#c9a24a] rounded-xl shadow-xl p-6 w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-stock-dialog"
      >
        <div className="flex items-center gap-2 mb-1">
          <Crown size={18} weight="fill" className="text-[#c9a24a]" />
          <h2 className="text-lg font-semibold">{t("distributors.edit_stock_title")}</h2>
        </div>
        <p className="text-sm text-zinc-600 mb-4">
          {t("distributors.edit_stock_subtitle", { name: product.name, current: product.current_stock })}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">
              {t("distributors.new_stock_label", { unit: product.unit || t("common.units") })}
            </label>
            <BrutalInput
              type="number"
              min="0"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid="edit-stock-input"
              autoFocus
            />
          </div>
          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={busy}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" variant="eliteFilled" className="flex-1" disabled={busy} data-testid="edit-stock-submit">
              {busy ? t("common.saving") : t("common.save")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function PaymentStatusBadge({ status }) {
  const { t } = useTranslation();
  const isPaid = status === "paid";
  return (
    <span
      data-testid={`payment-status-badge-${status}`}
      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border-2 ${
        isPaid
          ? "bg-white text-[#8a6a1c] border-[#c9a24a]"
          : "bg-[#c9a24a] text-white border-[#b48a2b]"
      }`}
    >
      <Crown size={11} weight="fill" />
      {isPaid ? t("distributors.status_paid") : t("distributors.status_consignment")}
    </span>
  );
}

function HeaderPaymentToggle({ distributor, onChanged }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const current = distributor.payment_status || "consignment";
  const flip = async (next) => {
    if (next === current || busy) return;
    setBusy(true);
    try {
      await api.patch(`/distributors/${distributor.id}/payment-status`, { status: next });
      toast.success(
        next === "paid"
          ? t("distributors.marked_paid", { name: distributor.name })
          : t("distributors.marked_consignment", { name: distributor.name })
      );
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="inline-flex rounded-lg border-2 border-white/60 bg-white/10 backdrop-blur p-0.5 text-xs font-semibold"
      data-testid="header-payment-toggle"
    >
      <button
        type="button"
        disabled={busy}
        onClick={() => flip("consignment")}
        data-testid="header-payment-consignment"
        className={`h-7 px-3 rounded-md transition-colors ${
          current === "consignment"
            ? "bg-[#c9a24a] text-white shadow-sm"
            : "text-white/90 hover:bg-white/20"
        }`}
      >
        {t("distributors.status_consignment")}
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => flip("paid")}
        data-testid="header-payment-paid"
        className={`h-7 px-3 rounded-md transition-colors ${
          current === "paid"
            ? "bg-white text-[#8a6a1c] shadow-sm"
            : "text-white/90 hover:bg-white/20"
        }`}
      >
        {t("distributors.status_paid")}
      </button>
    </div>
  );
}


function QuickGiveDialog({ distributorId, distributorName, onClose, onDone }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // v400.61: partners/distributors quick-give from their OWN held stock.
  // Self-consign (giving to yourself) keeps the master list — that's how a
  // super-owner partner pulls master stock into their own consignment.
  const useOwnHeld =
    (user?.role === "partner" || user?.role === "distributor") && user?.id !== distributorId;
  const [masters, setMasters] = useState([]);
  const [productId, setProductId] = useState("");
  const [transferType, setTransferType] = useState("consignment");
  const [quantity, setQuantity] = useState(1);
  const [recipient, setRecipient] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [loadingMasters, setLoadingMasters] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (useOwnHeld ? api.get(`/distributors/${user.id}/inventory`) : api.get("/products"))
      .then(({ data }) => {
        const inStock = (data || []).filter((p) => p.current_stock > 0);
        setMasters(inStock);
        if (inStock[0]) setProductId(inStock[0].id);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoadingMasters(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const picked = masters.find((m) => m.id === productId);
  const isSample = transferType === "sample";
  // v44: tiered SRP for Precision Nasal Mist sales.
  const tiers = useNasalTiers();
  const pickedMist = isNasalMist(picked);
  const [priceDirty, setPriceDirty] = useState(false);

  const autoPrice = (m, qty) => {
    if (isNasalMist(m)) {
      const p = tierUnitPrice(tiers, qty);
      if (p != null) return String(p);
    }
    return m && m.suggested_retail_price != null ? String(m.suggested_retail_price) : "";
  };

  const handlePickProduct = (newId) => {
    setProductId(newId);
    if (transferType === "sale") {
      const m = masters.find((mm) => mm.id === newId);
      setPriceDirty(false);
      setSalePrice(autoPrice(m, quantity));
    }
  };

  const handleTransferType = (t) => {
    setTransferType(t);
    if (t === "sale" && picked) {
      setPriceDirty(false);
      setSalePrice(autoPrice(picked, quantity));
    }
  };

  const handleQtyChange = (v) => {
    setQuantity(v);
    if (transferType === "sale" && pickedMist && !priceDirty) {
      const p = tierUnitPrice(tiers, v);
      if (p != null) setSalePrice(String(p));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!picked) {
      toast.error(t("distributors.quick_give_pick_product"));
      return;
    }
    const qty = Math.max(1, Number(quantity) || 0);
    if (qty > picked.current_stock) {
      toast.error(t("assign.only_n_on_hand", { n: picked.current_stock }));
      return;
    }

    if (isSample) {
      // Sample from OWNER's master stock — recipient name required, no distributor needed
      // BUT we surface from the distributor detail page as a courtesy entrypoint;
      // we still log it under the caller (owner), not the distributor.
      const name = recipient.trim();
      if (!name) {
        toast.error(t("assign.recipient_required"));
        return;
      }
      setBusy(true);
      try {
        await api.post("/products/mark-sample", {
          product_id: picked.id,
          quantity: qty,
          recipient_name: name,
          holder_user_id: useOwnHeld ? user.id : undefined,
        });
        toast.success(t("assign.sampled_n", { n: qty, name: picked.name }));
        onDone();
      } catch (e2) {
        toast.error(formatApiError(e2));
      } finally {
        setBusy(false);
      }
      return;
    }

    setBusy(true);
    try {
      const payload = {
        distributor_id: distributorId,
        product_id: picked.id,
        quantity: qty,
        transfer_type: transferType,
      };
      if (transferType === "sale") {
        const sp = salePrice === "" ? null : Number(salePrice);
        if (sp !== null && (!Number.isFinite(sp) || sp < 0)) {
          toast.error(t("assign.invalid_sale_price"));
          setBusy(false);
          return;
        }
        if (sp !== null) payload.sale_price = sp;
      }
      await api.post("/products/assign", payload);
      toast.success(
        transferType === "sale"
          ? t("assign.sold_n", { n: qty, name: picked.name })
          : t("assign.given_n", { n: qty, name: picked.name })
      );
      onDone();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="quick-give-dialog"
      >
        <h2 className="text-2xl font-semibold mb-1">
          {t("distributors.quick_give_title", { name: distributorName })}
        </h2>
        <p className="text-sm text-zinc-600 mb-4">{t("distributors.quick_give_subtitle")}</p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("distributors.product")}</label>
            {loadingMasters ? (
              <div className="text-sm text-zinc-500">{t("common.loading")}</div>
            ) : masters.length === 0 ? (
              <div className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
                {t("distributors.no_masters_in_stock")}
              </div>
            ) : (
              <select
                className="input-brutal"
                value={productId}
                onChange={(e) => handlePickProduct(e.target.value)}
                data-testid="quick-give-product-select"
              >
                {masters.map((m) => (
                  <option key={m.id} value={m.id}>
                    {`${m.name} — ${m.current_stock} ${m.unit || ""}`.trim()}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">{t("assign.transfer_type")}</label>
            <div className="grid grid-cols-3 gap-2" data-testid="quick-give-transfer-type">
              <button
                type="button"
                onClick={() => handleTransferType("consignment")}
                data-testid="quick-give-type-consignment"
                className={`h-10 rounded-lg border text-sm font-medium transition-colors ${
                  transferType === "consignment"
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                }`}
              >
                {t("assign.consign")}
              </button>
              <button
                type="button"
                onClick={() => handleTransferType("sale")}
                data-testid="quick-give-type-sale"
                className={`h-10 rounded-lg border text-sm font-medium transition-colors ${
                  transferType === "sale"
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                }`}
              >
                {t("assign.mark_sold")}
              </button>
              <button
                type="button"
                onClick={() => handleTransferType("sample")}
                data-testid="quick-give-type-sample"
                className={`h-10 rounded-lg border text-sm font-medium transition-colors ${
                  transferType === "sample"
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                }`}
              >
                {t("assign.mark_sample")}
              </button>
            </div>
          </div>

          {isSample && (
            <div>
              <label className="block text-xs font-semibold mb-1">{t("assign.recipient_name")}</label>
              <BrutalInput
                type="text"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder={t("assign.recipient_placeholder")}
                maxLength={200}
                data-testid="quick-give-recipient-input"
                autoFocus
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold mb-1">{t("assign.quantity")}</label>
            <BrutalInput
              type="number"
              min="1"
              max={picked?.current_stock || 1}
              value={quantity}
              onChange={(e) => handleQtyChange(e.target.value)}
              data-testid="quick-give-quantity-input"
            />
            {picked && (
              <p className="text-[11px] text-zinc-500 mt-1">
                {t("assign.dialog_subtitle", { name: picked.name, n: picked.current_stock })}
              </p>
            )}
          </div>

          {transferType === "sale" && pickedMist && <TierBreakdown tiers={tiers} qty={quantity} />}

          {transferType === "sale" && picked && (
            <div>
              <label className="block text-xs font-semibold mb-1">
                {t("assign.sale_price_label", { unit: picked.unit || t("common.units") })}
              </label>
              <BrutalInput
                type="number"
                min="0"
                step="0.01"
                value={salePrice}
                onChange={(e) => {
                  setPriceDirty(true);
                  setSalePrice(e.target.value);
                }}
                placeholder={picked.suggested_retail_price != null ? String(picked.suggested_retail_price) : "0.00"}
                data-testid="quick-give-sale-price-input"
              />
              <PriceChips
                wholesale={picked.wholesale_price}
                retail={picked.suggested_retail_price}
                value={salePrice}
                onPick={(v) => { setPriceDirty(true); setSalePrice(v); }}
                testIdPrefix="quick-give-price-chip"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                {picked.suggested_retail_price != null
                  ? t("assign.sale_price_hint_srp", { srp: picked.suggested_retail_price })
                  : t("assign.sale_price_hint_no_srp")}
              </p>
              <div className="mt-2">
                <SaleSummary qty={quantity} unitPrice={salePrice} wholesale={picked.wholesale_price} />
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton
              type="submit"
              className="flex-1"
              disabled={busy || !picked}
              data-testid="quick-give-submit"
            >
              {busy ? t("assign.sending") : isSample ? t("assign.log_sample") : t("assign.give")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}
